import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";

import {
  regFieldControls,
  weatherStationControls,
  customTranslation,
  fieldViewControls,
  waterSourceControls,
  poolsControls,
  growingSeasonControls,
} from "./configs/geoman-configs";
import CancelIcon from "../../public/assets/icons/cancel.png";
import ConfirmIcon from "../../public/assets/icons/confirm.png";
import Location from "../../public/assets/icons/location.png";
import { getCoordinates, setPopup } from "./utils/polygonUtil";
import { getMyLocation } from "./utils/locator";
import {
  FarmerFieldsStates,
  fieldsColorHelper,
  Service,
} from "@helpers/ui_helper";
import {
  CustomMarker,
  FieldMarkerIcon,
  samplingStationIcon,
  waterMarkerIcon,
} from "./utils/customs";
import { LatLng } from "spherical-geometry-js";
import TimeLine from "./components/timeline";
import { Button, Tour } from "antd";
import { mapSteps } from "../../tour_steps/fields";
import Coordinate from "../coordinate/coordinate";
import { QuestionCircleOutlined } from "@ant-design/icons";

export default function Map({
  hidden,
  service,
  setStationObject,
  setFarmerFieldsState,
  setFarmerMapFieldData,
  stationObject,
  fieldObject,
  stationMarkerCoordinates,
  waterSourceMarkerCoordinates,
  setWaterSourceObject,
  handleRatingChange,
  polygonCoordinates,
  farmerFields,
  productId,
  cityId,
  hsitoricalFields,
  index,
  poolMarkerCoordinates,
  setPoolObject,
  samplingStationPoints,
  serviceReport,
  setUpdatedPolygonData,
  setShowPolygonDrawing,
}) {
  const [open, setOpen] = useState(false);
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const ref4 = useRef(null);
  const [polygonData, setPolygonData] = useState({
    polygon: [],
    centerCoordinates: [],
    area: 0,
  });
  // const googleSatelliteUrl = "http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
  const googleHybridUrl = "http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}";
  const attribution = "google | agroyaar";
  const maxZoom = 20;
  const subdomains = ["mt0", "mt1", "mt2", "mt3"];
  let map = null;
  let marker = null;
  const coordinateRef = useRef(null);
  useLayoutEffect(() => {
    var container = L.DomUtil.get(`map${index}`);
    if (container != null) {
      container._leaflet_id = null;
    }
    map = new L.Map(`map${index}`, {
      center: stationMarkerCoordinates
        ? new L.LatLng(stationMarkerCoordinates[0], stationMarkerCoordinates[1])
        : poolMarkerCoordinates
        ? new L.LatLng(poolMarkerCoordinates[0], poolMarkerCoordinates[1])
        : waterSourceMarkerCoordinates
        ? new L.LatLng(
            waterSourceMarkerCoordinates[0],
            waterSourceMarkerCoordinates[1]
          )
        : new L.LatLng(35.5868, 59.35469),
      zoom: 14,
      zoomControl:
        service === Service.WeatherActivation ||
        service === Service.SetFieldInfos ||
        service === Service.FieldView ||
        service === Service.MyFields ||
        service === Service.HistoricalMaps ||
        service === Service.WaterSourcesView ||
        service === Service.PoolsView ||
        service === Service.ChoosePolygonGrowingSeasonField
          ? false
          : true,
    });
    coordinateRef.current = (e) =>
      fieldObject?.centerCoordinates?.length > 0 ? null : map?.flyTo(e);
    const gglHybrid = L.tileLayer(googleHybridUrl, {
      maxZoom: maxZoom,
      attribution: attribution,
      subdomains: subdomains,
    });
    gglHybrid.addTo(map);
    map.pm.setLang("editedPersian", customTranslation, "fa");

    if (service === Service.Weather) {
      map.pm.addControls(weatherStationControls);

      stationMarkerCoordinates &&
        addMarker(
          service,
          new L.LatLng(stationMarkerCoordinates[0], stationMarkerCoordinates[1])
        );

      map.on("click", (e) => {
        addMarker(service, e?.latlng);
      });
    } else if (service === Service.WaterSources) {
      map.pm.addControls(waterSourceControls);

      waterSourceMarkerCoordinates &&
        addMarker(
          service,
          new L.LatLng(
            waterSourceMarkerCoordinates[0],
            waterSourceMarkerCoordinates[1]
          )
        );

      map.on("click", (e) => {
        addMarker(service, e?.latlng);
      });
    } else if (service === Service.Pools) {
      map.pm.addControls(poolsControls);

      poolMarkerCoordinates &&
        addMarker(
          service,
          new L.LatLng(poolMarkerCoordinates[0], poolMarkerCoordinates[1])
        );

      map.on("click", (e) => {
        addMarker(service, e?.latlng);
      });
    } else if (service === Service.WeatherActivation) {
      map.pm.addControls(weatherStationControls);
      new L.marker(stationMarkerCoordinates, {
        icon: CustomMarker,
      }).addTo(map);
    } else if (service === Service.WaterSourcesView) {
      map.pm.addControls(waterSourceControls);
      new L.marker(waterSourceMarkerCoordinates, {
        icon: waterMarkerIcon,
      }).addTo(map);
      // map.flyTo(waterSourceMarkerCoordinates)
    } else if (service === Service.PoolsView) {
      map.pm.addControls(poolsControls);
      new L.marker(poolMarkerCoordinates, {
        icon: waterMarkerIcon,
      }).addTo(map);
      // map.flyTo(waterSourceMarkerCoordinates)
    } else if (service === Service.FieldRegistration) {
      map.pm.addControls(regFieldControls);
      if (polygonCoordinates?.length > 0) {
        let latLngTypePolygonCoordinates = [];

        if (Array.isArray(polygonCoordinates[0])) {
          latLngTypePolygonCoordinates = polygonCoordinates?.map(
            (pc) => new LatLng(pc[0], pc[1])
          );
        } else {
          latLngTypePolygonCoordinates = polygonCoordinates;
        }

        map.pm.Toolbar.setButtonDisabled("drawPolygon", true);
        map.pm.Toolbar.setButtonDisabled("removalMode", true);
        const geoJson = {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [latLngTypePolygonCoordinates],
          },
        };

        const polygon = L.geoJSON(geoJson).addTo(map);
        map.fitBounds(polygon.getBounds());

        map.pm.enableGlobalEditMode("Polygon", {
          snappable: true,
          allowSelfIntersection: false,

          editable: true,
          templineStyle: { color: "red" },
          hintlineStyle: { color: "red", dashArray: [5, 5] },
        });

        map.pm.setGlobalOptions({
          pinning: true,
          allowSelfIntersection: false,
          allowSelfIntersectionEdit: false,
          snappable: false,
          pmIgnore: false,
        });

        map.pm.getGeomanLayers()?.map((layer, index) => {
          layer.setStyle({ pmIgnore: false });
          layer.pm.enable({
            pinning: true,
            allowIntersection: false,
            snappable: false,
          });
          layer.on("pm:edit", (e) => {
            const drawPolygans = map.pm.getGeomanLayers(true).toGeoJSON()
              .features[0].geometry.coordinates[0];

            setPopup(drawPolygans, setPolygonData);
          });
        });
      }

      map.on("pm:create", ({ layer }) => {
        map.pm.Toolbar.setButtonDisabled("drawPolygon", true);

        setPopup(getCoordinates(layer), setPolygonData);

        layer.on("pm:edit", ({ layer }) => {
          setPopup(getCoordinates(layer), setPolygonData);
        });

        layer.on("pm:remove", ({ layer }) => {
          map.pm.Toolbar.setButtonDisabled("drawPolygon", false);
        });
      });
    } else if (service === Service.GrowingSeasonField) {
      map.pm.addControls(growingSeasonControls);
      if (polygonCoordinates?.length > 0) {
        let latLngTypePolygonCoordinates = [];

        if (Array.isArray(polygonCoordinates[0])) {
          latLngTypePolygonCoordinates = polygonCoordinates?.map(
            (pc) => new LatLng(pc[0], pc[1])
          );
        } else {
          latLngTypePolygonCoordinates = polygonCoordinates;
        }

        map.pm.Toolbar.setButtonDisabled("drawPolygon", true);
        map.pm.Toolbar.setButtonDisabled("removalMode", true);
        const geoJson = {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [latLngTypePolygonCoordinates],
          },
        };

        // to enable cancel edit
        setPopup(latLngTypePolygonCoordinates);

        const polygon = L.geoJSON(geoJson).addTo(map);
        map.fitBounds(polygon.getBounds());

        map.pm.enableGlobalEditMode("Polygon", {
          snappable: true,
          allowSelfIntersection: false,
          editable: true,
          templineStyle: { color: "red" },
          hintlineStyle: { color: "red", dashArray: [5, 5] },
        });

        map.pm.setGlobalOptions({
          pinning: true,
          allowSelfIntersection: false,
          allowSelfIntersectionEdit: false,
          snappable: false,
          pmIgnore: false,
        });

        map.pm.getGeomanLayers()?.map((layer, index) => {
          layer.setStyle({ pmIgnore: false });
          layer.pm.enable({
            pinning: true,
            allowIntersection: false,
            snappable: false,
          });
          layer.on("pm:edit", (e) => {
            const drawPolygans = map.pm.getGeomanLayers(true).toGeoJSON()
              .features[0].geometry.coordinates[0];
            setPopup(drawPolygans, setPolygonData);
          });
        });
      }

      map.on("pm:create", ({ layer }) => {
        map.pm.Toolbar.setButtonDisabled("drawPolygon", true);
        setPopup(getCoordinates(layer), setPolygonData);

        layer.on("pm:edit", ({ layer }) => {
          setPopup(getCoordinates(layer), setPolygonData);
        });

        layer.on("pm:remove", ({ layer }) => {
          map.pm.Toolbar.setButtonDisabled("drawPolygon", false);
        });
      });
    } else if (service === Service.GrowingSeasonField_ShowConfirm) {
      map.pm.addControls(regFieldControls);
      if (polygonCoordinates?.length > 0) {
        let latLngTypePolygonCoordinates = [];

        if (Array.isArray(polygonCoordinates[0])) {
          latLngTypePolygonCoordinates = polygonCoordinates?.map(
            (pc) => new LatLng(pc[0], pc[1])
          );
        } else {
          latLngTypePolygonCoordinates = polygonCoordinates;
        }

        map.pm.Toolbar.setButtonDisabled("drawPolygon", true);
        map.pm.Toolbar.setButtonDisabled("removalMode", true);
        const geoJson = {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [latLngTypePolygonCoordinates],
          },
        };

        const polygon = L.geoJSON(geoJson).addTo(map);
        map.fitBounds(polygon.getBounds());

        map.pm.enableGlobalEditMode("Polygon", {
          snappable: true,
          allowSelfIntersection: false,

          editable: true,
          templineStyle: { color: "red" },
          hintlineStyle: { color: "red", dashArray: [5, 5] },
        });

        map.pm.setGlobalOptions({
          pinning: true,
          allowSelfIntersection: false,
          allowSelfIntersectionEdit: false,
          snappable: false,
          pmIgnore: false,
        });

        map.pm.getGeomanLayers()?.map((layer, index) => {
          layer.setStyle({ pmIgnore: false });
          layer.pm.enable({
            pinning: true,
            allowIntersection: false,
            snappable: false,
          });
          layer.on("pm:edit", (e) => {
            const drawPolygans = map.pm.getGeomanLayers(true).toGeoJSON()
              .features[0].geometry.coordinates[0];

            setPopup(drawPolygans, setPolygonData);
          });
        });
      }

      map.on("pm:create", ({ layer }) => {
        map.pm.Toolbar.setButtonDisabled("drawPolygon", true);

        setPopup(getCoordinates(layer), setPolygonData);

        layer.on("pm:edit", ({ layer }) => {
          setPopup(getCoordinates(layer), setPolygonData);
        });

        layer.on("pm:remove", ({ layer }) => {
          map.pm.Toolbar.setButtonDisabled("drawPolygon", false);
        });
      });
    } else if (
      service === Service.SetFieldInfos ||
      service === Service.FieldView ||
      service === Service.ChoosePolygonGrowingSeasonField
    ) {
      if (polygonCoordinates?.length > 0) {
        let latLngTypePolygonCoordinates = [];

        if (Array.isArray(polygonCoordinates[0])) {
          latLngTypePolygonCoordinates = polygonCoordinates?.map(
            (pc) => new LatLng(pc[0], pc[1])
          );
        } else {
          latLngTypePolygonCoordinates = polygonCoordinates;
        }

        map.pm.addControls(fieldViewControls);
        const geoJson = {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [latLngTypePolygonCoordinates],
          },
        };

        const polygon = L.geoJSON(geoJson)
          .setStyle({ color: fieldsColorHelper(productId) })
          .addTo(map);
        map.fitBounds(polygon.getBounds());
      }
      if (samplingStationPoints?.length > 0) {
        samplingStationPoints?.map((station, i) => {
          L.marker([station?.latitude, station?.longitude], {
            icon: samplingStationIcon(serviceReport, i),
          }).addTo(map);
        });
      }
    } else if (service === Service.MyFields) {
      const fieldsPolygons = farmerFields?.map((farm) => farm?.polygon);
      const pids = farmerFields?.map((farm) => farm?.productId);
      const centers = farmerFields?.map((farm) => farm?.centerCoordinates);
      let latLngTypePolygonCoordinates = fieldsPolygons?.map((fieldPolygon) =>
        fieldPolygon?.map((points) => new LatLng(points[0], points[1]))
      );

      const polygonGroup = new L.featureGroup().addTo(map);

      latLngTypePolygonCoordinates.map((item, index) => {
        const geoJson = {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [item],
          },
        };
        new L.marker(centers[index], { icon: FieldMarkerIcon }).addTo(map);
        L.geoJSON(geoJson)
          .setStyle({ color: fieldsColorHelper(pids[index]) })
          .addTo(polygonGroup);
      });
      try {
        map.fitBounds(polygonGroup.getBounds());
      } catch (error) {}
    } else if (service === Service.HistoricalMaps) {
    }

    const x = document.getElementsByClassName(
      "control-icon leaflet-pm-icon-polygon"
    )[0];
    ref2.current = x;
    const y = document.getElementsByClassName(
      "control-icon leaflet-pm-icon-edit"
    )[0];
    ref3.current = y;

    const z = document.getElementsByClassName(
      "control-icon leaflet-pm-icon-delete"
    )[0];
    ref4.current = z;
  }, [polygonCoordinates, productId]);

  const addMarker = (service, latLng) => {
    marker && marker.remove();
    if (service === Service.Weather) {
      marker = new L.marker(latLng, { icon: CustomMarker }).addTo(map);
    }
    if (service === Service.WaterSources || service === Service.Pools) {
      marker = new L.marker(latLng, { icon: waterMarkerIcon }).addTo(map);
    }
    if (service === Service.FieldRegistration) {
      marker = new L.marker(latLng, { icon: FieldMarkerIcon }).addTo(map);
    }
    const coordinates = new L.LatLng(latLng?.wrap().lat, latLng?.wrap().lng);
    if (service === Service.Weather) {
      setStationObject((prev) => ({
        ...prev,
        location: [coordinates?.lat, coordinates?.lng],
      }));
    }
    if (service === Service.WaterSources) {
      handleRatingChange([coordinates?.lat, coordinates?.lng]);
    }
    if (service === Service.Pools) {
      setPoolObject((prev) => ({
        ...prev,
        location: [coordinates?.lat, coordinates?.lng],
      }));
    }
    const popupElement = document.getElementById("popup");
    popupElement.setAttribute("style", "display:flex");
    popupElement.childNodes[0].textContent = `${coordinates?.lng?.toFixed(
      6
    )}, ${coordinates?.lat?.toFixed(6)}`;
  };

  const back = () => {
    setFarmerFieldsState((prev) => ({
      ...prev,
      service: Service.SetFieldInfos,
      state: FarmerFieldsStates.FieldDetails,
    }));
  };

  const savePolygonDataToFieldObject = () => {
    setFarmerFieldsState((prev) => ({
      ...prev,
      service: Service.SetFieldInfos,
      state: FarmerFieldsStates.FieldDetails,
      fieldObject: {
        ...fieldObject,
        polygon: polygonData?.polygon,
        centerCoordinates: polygonData?.centerCoordinates,
        area: polygonData?.area,
      },
    }));
  };

  let polygon = null;

  const showMyPolygon = (index) => {
    polygon && map.removeLayer(polygon);

    const myPolygon = hsitoricalFields[index]?.polygon;
    const productId = hsitoricalFields[index]?.productId;

    const latLngTypePolygonCoordinates = myPolygon?.map(
      (point) => new LatLng(point[0], point[1])
    );

    map.pm.addControls(fieldViewControls);
    const geoJson = {
      type: "Feature",
      properties: {},
      geometry: {
        type: "Polygon",
        coordinates: [latLngTypePolygonCoordinates],
      },
    };

    polygon = L.geoJSON(geoJson)
      .setStyle({ color: fieldsColorHelper(productId) })
      .addTo(map);

    map.fitBounds(polygon.getBounds());
  };

  useEffect(() => {
    if (polygonData && setUpdatedPolygonData) {
      setUpdatedPolygonData({
        polygon: polygonData.polygon.map((item) => [
          item?.latitude,
          item?.longitude,
        ]),
        centerCoordinates: polygonData.centerCoordinates,
        area: polygonData.area,
      });
    }
  }, [polygonData]);

  return (
    <div className="relative h-full w-full rounded-lg font-body">
      {/* map */}
      <div id={`map${index}`} className="h-full w-full rounded-lg" />
      {/* confirmation controls */}

      {(service === Service.FieldRegistration ||
        service === Service.Pools ||
        service === Service.WaterSources ||
        service === Service.GrowingSeasonField) && (
        <div
          id="searchAndHelp"
          className="flex max-lg:flex-col absolute gap-4 max-lg:gap-1 left-1/2 transform -translate-x-1/2 top-4 z-[999]"
        >
          <Coordinate
            hidden={hidden}
            ref1={ref1}
            findCity={(e) => coordinateRef.current(e)}
            fieldObject={fieldObject}
          />
        </div>
      )}

      {service === Service.FieldRegistration && (
        <div
          id="searchAndHelp"
          className="flex max-lg:flex-col absolute gap-4 max-lg:gap-1 left-0 max-lg:w-40 top-4 z-[999] "
        >
          <Button
            className="circle-neo flex items-center justify-center text-[#008a44] hover:!text-[#50aa44] font-bold !rounded-xl max-lg:w-16 max-lg:absolute left-0 top-20 !rounded-l-none"
            icon={<QuestionCircleOutlined className="text-base" />}
            onClick={() => setOpen(true)}
          >
            راهنما
          </Button>
        </div>
      )}
      {/* {service === Service.Weather && (
        <div
          id="searchAndHelp"
          className="flex absolute gap-4 max-lg:gap-1 left-52 max-lg:left-16 top-4 z-[999] "
        >
           
            <Coordinate
              map={map}
              findCity={(e) => coordinateRef.current(e)}
              fieldObject={fieldObject}
            />
          
        </div>
      )} */}
      {(service === Service.FieldRegistration ||
        service === Service.GrowingSeasonField_ShowConfirm) && (
        <div
          id="confirmationControls"
          className="flex absolute gap-4 max-lg:gap-1 right-4 bottom-4 z-[999]"
        >
          <div
            onClick={
              service === Service.FieldRegistration
                ? back
                : () => setShowPolygonDrawing((prev) => !prev)
            }
            id="cancelButton"
            className="flex flex-row-reverse justify-between items-center bg-[#E9F0F7] h-9 w-20 max-lg:w-24 rounded-2xl px-3 cursor-pointer"
          >
            <img
              id="cancelIcon"
              alt="cancel-icon"
              src={CancelIcon}
              className="h-8 w-8 max-lg:w-4 max-lg:h-4"
            />
            <p>لغو</p>
          </div>
          {polygonData?.polygon?.length !== 0 ? (
            <div
              onClick={
                service === Service.FieldRegistration
                  ? savePolygonDataToFieldObject
                  : () => setShowPolygonDrawing((prev) => !prev)
              }
              id="confirmButton"
              className="flex flex-row-reverse justify-between items-center bg-[#E9F0F7] h-9 w-20 max-lg:w-24 rounded-full px-3 cursor-pointer"
            >
              <img
                id="confirmIcon"
                alt="confirm-icon"
                src={ConfirmIcon}
                className="h-8 w-8 max-lg:w-4 max-lg:h-4"
              />
              <p>تایید</p>
            </div>
          ) : null}
        </div>
      )}

      {/* popUp area */}
      <div
        id="popup"
        className="absolute hidden h-8 w-64 max-lg:w-fit max-lg:bottom-4 gap-4 left-5 bottom-3 z-[999] bg-[#E9F0F7] rounded-full justify-start items-center max-lg:pl-8 px-4"
      >
        <p></p>
      </div>

      {/* location finder */}
      {service !== Service.WeatherActivation &&
        service !== Service.WaterSources &&
        service !== Service.SetFieldInfos &&
        service !== Service.FieldView &&
        service !== Service.MyFields &&
        service !== Service.ChoosePolygonGrowingSeasonField &&
        service !== Service.WaterSourcesView &&
        service !== Service.PoolsView &&
        service !== Service.Pools &&
        service !== Service.HistoricalMaps && (
          <div
            id="locator"
            className="circle-neo absolute flex justify-center items-center p-2 left-2 bottom-2.5 max-lg:bottom-3 max-lg:left-2 h-10 w-10 rounded-full z-[999] cursor-pointer"
            onClick={() => getMyLocation(map)}
          >
            <img src={Location} alt="location-image" />
          </div>
        )}

      {/* timeLine */}
      {service === Service.HistoricalMaps && (
        <TimeLine
          hsitoricalFields={hsitoricalFields}
          showMyPolygon={showMyPolygon}
        />
      )}
      <Tour
        type="primary"
        className="!bg-green-500"
        open={open}
        steps={mapSteps(ref1, ref2, ref3, ref4)}
        onClose={() => {
          setOpen(false);
        }}
      />
    </div>
  );
}
